/**
 * HeadTremorDetector — measure micro-vibration amplitude on the nose-tip
 * face landmark within a sliding window.
 *
 * Face landmarker runs at ~30 fps, so Nyquist is 15 Hz — comfortably covers
 * physiological head tremor at 2–6 Hz. Uses the same detrending +
 * amplitude-saturation pattern as HandTremorDetector but on a single
 * landmark (nose tip, MediaPipe face-mesh idx 1).
 *
 * Output interface mirrors HandTremor (amplitudePx/freqHz/count/confidence)
 * so HUD/UI code can treat both signals symmetrically. AI Proctor 6원칙 #2
 * (오탐 전제) — single boolean ❌, evidence record with confidence ✓.
 */

export interface FaceLandmark { x: number; y: number; z?: number }

export interface TremorMetric {
  /** Detrended nose-tip stddev × image side length, in image pixels. */
  amplitudePx: number;
  /** Dominant frequency in Hz (zero-crossing rate of detrended Y). */
  freqHz: number;
  /** Number of samples accumulated in the window. */
  count: number;
  /** 0..1 — saturates when amplitude crosses tremorAmplitudeThresh. */
  confidence: number;
}

const ZERO: TremorMetric = { amplitudePx: 0, freqHz: 0, count: 0, confidence: 0 };

const NOSE_TIP_IDX = 1;

export class HeadTremorDetector {
  private readonly windowMs: number;
  private readonly amplitudeThreshPx: number;
  private ring: Array<{ x: number; y: number; t: number }> = [];

  constructor(windowMs = 1000, amplitudeThreshPx = 3) {
    this.windowMs = windowMs;
    this.amplitudeThreshPx = amplitudeThreshPx;
  }

  reset(): void { this.ring = []; }

  update(
    landmarks: FaceLandmark[] | null | undefined,
    now: number,
    imageSidePx: number,
  ): TremorMetric {
    if (!landmarks || landmarks.length <= NOSE_TIP_IDX) {
      // Face lost → reset ring. Stale samples from before disappearance
      // produce misleading metrics (mirrors HandTremor frame_005 fix).
      this.reset();
      return ZERO;
    }
    const nose = landmarks[NOSE_TIP_IDX];
    if (!nose) {
      this.reset();
      return ZERO;
    }
    this.ring.push({ x: nose.x, y: nose.y, t: now });
    this.prune(now);
    return this.compute(imageSidePx);
  }

  private prune(now: number): void {
    const cutoff = now - this.windowMs;
    while (this.ring.length > 0 && this.ring[0].t < cutoff) this.ring.shift();
  }

  private compute(imageSidePx: number): TremorMetric {
    const minSamples = 10;
    // Above this amplitude the motion is dominated by intentional head
    // movement (looking around), not tremor — drop confidence to 0.
    const motionAmplitudePx = imageSidePx * 0.015;

    if (this.ring.length < minSamples) {
      return { ...ZERO, count: this.ring.length };
    }

    // Detrend with 3-sample triangular kernel to isolate high-freq tremor
    // from low-freq intentional motion.
    const W = 3;
    const detrendX: number[] = [];
    const detrendY: number[] = [];
    for (let k = 0; k < this.ring.length; k++) {
      let sumX = 0, sumY = 0, n = 0;
      for (let j = Math.max(0, k - W); j <= Math.min(this.ring.length - 1, k + W); j++) {
        sumX += this.ring[j].x; sumY += this.ring[j].y; n++;
      }
      detrendX.push(this.ring[k].x - sumX / n);
      detrendY.push(this.ring[k].y - sumY / n);
    }

    let vx = 0, vy = 0;
    for (let k = 0; k < this.ring.length; k++) {
      vx += detrendX[k] ** 2;
      vy += detrendY[k] ** 2;
    }
    const stdX = Math.sqrt(vx / this.ring.length);
    const stdY = Math.sqrt(vy / this.ring.length);
    // 2D radial std → px (normalized coords × image side)
    const amplitudePx = Math.sqrt(stdX * stdX + stdY * stdY) * imageSidePx;

    // Zero-crossing on detrended Y (head nodding has stronger vertical
    // component than horizontal). Hz = crossings / (2 × span_seconds).
    let zc = 0;
    for (let k = 1; k < detrendY.length; k++) {
      if (detrendY[k - 1] * detrendY[k] < 0) zc++;
    }
    const spanSec = (this.ring[this.ring.length - 1].t - this.ring[0].t) / 1000;
    const freqHz = spanSec > 0 ? zc / (2 * spanSec) : 0;

    let confidence: number;
    if (amplitudePx >= motionAmplitudePx) {
      confidence = 0;
    } else {
      confidence = Math.min(1, amplitudePx / this.amplitudeThreshPx);
    }
    // Physiological head tremor band: 2–6 Hz. Outside this band the signal
    // is likely scanning/nodding (low) or noise (high) — reduce confidence.
    if (freqHz < 2 || freqHz > 6) confidence *= 0.3;

    return {
      amplitudePx,
      freqHz,
      count: this.ring.length,
      confidence,
    };
  }
}
